import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", date: "", paid: true, branch_id: "all", description: "", active: true };

export default function Holidays() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const { data: holidays } = useQuery({ queryKey: ["holidays"], queryFn: async () => (await api.get("/holidays")).data });
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const branchName = (id) => (id === "all" || !id ? "All Branches" : branches?.find((b) => b.id === id)?.name || "-");

  const save = async () => {
    try {
      if (editing) await api.put(`/holidays/${editing.id}`, form);
      else await api.post("/holidays", form);
      toast.success("Saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["holidays"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete holiday?")) return; await api.delete(`/holidays/${id}`); qc.invalidateQueries({ queryKey: ["holidays"] }); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Government Holidays</h1>
        <Button data-testid="add-holiday-button" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="lg:hidden space-y-3">
        {(holidays || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No holidays yet.</div>}
        {(holidays || []).map((h) => (
          <div key={h.id} data-testid={`holiday-card-${h.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{h.name}</div><div className="text-xs text-slate-400 mt-0.5">{h.date}</div></div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => { setEditing(h); setForm(h); setOpen(true); }} className="p-2 text-slate-400 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(h.id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-slate-50 text-slate-600">{h.paid ? "Paid" : "Unpaid"}</span>
              <span className="px-2 py-1 rounded-full bg-slate-50 text-slate-600">{branchName(h.branch_id)}</span>
              <span className={`px-2 py-1 rounded-full ${h.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{h.active ? "Active" : "Inactive"}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Date</th>
            <th className="text-left px-4 py-3">Paid</th><th className="text-left px-4 py-3 hidden sm:table-cell">Branch</th>
            <th className="text-left px-4 py-3">Active</th><th></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(holidays || []).map((h) => (
              <tr key={h.id} data-testid={`holiday-row-${h.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{h.name}</td>
                <td className="px-4 py-3">{h.date}</td>
                <td className="px-4 py-3">{h.paid ? "Paid" : "Unpaid"}</td>
                <td className="px-4 py-3 hidden sm:table-cell">{branchName(h.branch_id)}</td>
                <td className="px-4 py-3">{h.active ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setEditing(h); setForm(h); setOpen(true); }} className="p-2 text-slate-400 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(h.id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Holiday" : "New Holiday"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Name</label><Input data-testid="holiday-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Date</label><Input data-testid="holiday-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <label className="text-xs text-slate-500">Applicable Branch</label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="holiday-branch-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Branches</SelectItem>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Description</label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.paid} onCheckedChange={(v) => setForm({ ...form, paid: v })} /> Paid Holiday</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
            <Button data-testid="save-holiday-button" onClick={save} disabled={!form.name || !form.date} className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
